import { Link, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Heart, Flag, Quote, Pin, Lock, EyeOff, Trash2, Edit2, Save, X } from "lucide-react";
import { api } from "@/lib/api";
import { isStaff as roleIsStaff } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import SteamLoginButton from "@/components/SteamLoginButton";

function fmt(iso) { try { return new Date(iso).toLocaleString("fr-FR"); } catch { return ""; } }

export default function ForumTopicPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isStaff = roleIsStaff(user);

  const [reply, setReply] = useState("");
  const [quoteId, setQuoteId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [reportContent, setReportContent] = useState("");
  const [reportTarget, setReportTarget] = useState(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["topic", id],
    queryFn: () => api.get(`/forum/topics/${id}`).then((r) => r.data),
    retry: false,
  });

  const submitReply = async () => {
    if (reply.trim().length < 1) return;
    try {
      await api.post(`/forum/topics/${id}/posts`, { content: reply, quote_post_id: quoteId });
      setReply(""); setQuoteId(null);
      toast.success("Réponse publiée");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const saveEdit = async (postId) => {
    try {
      await api.put(`/forum/posts/${postId}`, { content: editContent });
      toast.success("Message modifié");
      setEditing(null);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await api.delete(`/forum/posts/${postId}`);
      toast.success("Message supprimé");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const react = async (postId) => {
    try { await api.post(`/forum/posts/${postId}/react`); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const submitReport = async () => {
    if (reportContent.trim().length < 3 || !reportTarget) return;
    try {
      await api.post(`/forum/posts/${reportTarget}/report`, { reason: reportContent });
      toast.success("Signalement envoyé au staff");
      setReportContent(""); setReportTarget(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const moderate = async (action) => {
    try {
      await api.post(`/forum/topics/${id}/moderate?action=${action}`);
      toast.success("Action effectuée");
      if (action === "delete") { qc.invalidateQueries(); navigate(-1); }
      else refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (isLoading) return <div className="max-w-4xl mx-auto px-4 py-16 text-muted-foreground">Chargement...</div>;
  if (error) {
    const status = error?.response?.status;
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Card><CardContent className="p-8 text-center">
          <p className="font-display text-2xl uppercase mb-2">{status === 403 ? "Accès refusé" : status === 404 ? "Sujet introuvable" : "Erreur"}</p>
          <p className="text-muted-foreground text-sm">{status === 403 ? "Vous n'avez pas la permission d'accéder à ce sujet." : "Ce sujet n'existe pas ou a été supprimé."}</p>
          <Button asChild variant="outline" className="mt-4"><Link to="/forum">Retour au forum</Link></Button>
        </CardContent></Card>
      </div>
    );
  }

  const t = data.topic;
  const posts = data.posts;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-sm text-muted-foreground mb-2">
        <Link to={`/forum/c/${t.category_slug}`} className="hover:text-primary">← {t.category_slug}</Link>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="flex-1">
          <div className="flex flex-wrap gap-2 mb-2">
            {t.pinned && <Badge variant="outline" className="border-primary/50 text-primary"><Pin className="h-3 w-3 mr-1" /> Épinglé</Badge>}
            {t.locked && <Badge variant="outline"><Lock className="h-3 w-3 mr-1" /> Verrouillé</Badge>}
            {t.hidden && <Badge variant="destructive"><EyeOff className="h-3 w-3 mr-1" /> Masqué</Badge>}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">{t.title}</h1>
        </div>
        {isStaff && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="moderate-topic-button">Modération</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => moderate(t.pinned ? "unpin" : "pin")}>{t.pinned ? "Désépingler" : "Épingler"}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => moderate(t.locked ? "unlock" : "lock")}>{t.locked ? "Déverrouiller" : "Verrouiller"}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => moderate(t.hidden ? "unhide" : "hide")}>{t.hidden ? "Rendre visible" : "Masquer"}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => moderate("delete")} className="text-destructive">Supprimer le sujet</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="space-y-4">
        {posts.map((p, idx) => (
          <Card key={p.id} className="border-border" data-testid={`post-${p.id}`}>
            <CardContent className="p-5 grid sm:grid-cols-[auto_1fr] gap-4">
              <div className="flex sm:flex-col items-center sm:items-start gap-3 sm:min-w-[140px]">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={p.author.avatar_url} />
                  <AvatarFallback>{p.author.persona_name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">{p.author.persona_name}</p>
                  <p className="text-[10px] uppercase tracking-widest text-primary font-mono-tech">{p.author.role}</p>
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex justify-between items-center mb-2 text-xs text-muted-foreground">
                  <span className="font-mono-tech">#{idx + 1} · {fmt(p.created_at)}{p.updated_at !== p.created_at && " (modifié)"}</span>
                </div>
                {p.quote && (
                  <div className="border-l-2 border-primary/60 pl-3 mb-3 text-sm text-muted-foreground bg-secondary/50 rounded-r py-2">
                    <p className="font-medium text-foreground/80 text-xs mb-1">{p.quote.author.persona_name} a écrit :</p>
                    <p className="line-clamp-3">{p.quote.content}</p>
                  </div>
                )}
                {editing === p.id ? (
                  <div className="space-y-2">
                    <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={5} data-testid={`edit-textarea-${p.id}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(p.id)} data-testid={`save-edit-${p.id}`}><Save className="h-3.5 w-3.5 mr-1" />Enregistrer</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="h-3.5 w-3.5 mr-1" />Annuler</Button>
                    </div>
                  </div>
                ) : (
                  <p className="rp-prose text-sm">{p.content}</p>
                )}

                <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-border">
                  <Button variant="ghost" size="sm" onClick={() => user ? react(p.id) : toast.error("Connectez-vous pour réagir")} className={p.reacted ? "text-primary" : ""} data-testid={`react-${p.id}`}>
                    <Heart className={`h-3.5 w-3.5 mr-1 ${p.reacted ? "fill-primary" : ""}`} /> {p.reaction_count}
                  </Button>
                  {user && !t.locked && (
                    <Button variant="ghost" size="sm" onClick={() => { setQuoteId(p.id); document.getElementById("reply-box")?.scrollIntoView({ behavior: "smooth" }); }} data-testid={`quote-${p.id}`}>
                      <Quote className="h-3.5 w-3.5 mr-1" /> Citer
                    </Button>
                  )}
                  {p.editable && editing !== p.id && (
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(p.id); setEditContent(p.content); }} data-testid={`edit-${p.id}`}>
                      <Edit2 className="h-3.5 w-3.5 mr-1" /> Modifier
                    </Button>
                  )}
                  {isStaff && (
                    <Button variant="ghost" size="sm" onClick={() => deletePost(p.id)} className="text-destructive" data-testid={`delete-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer
                    </Button>
                  )}
                  {user && (
                    <Dialog open={reportTarget === p.id} onOpenChange={(o) => setReportTarget(o ? p.id : null)}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`report-${p.id}`}><Flag className="h-3.5 w-3.5 mr-1" /> Signaler</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Signaler ce message</DialogTitle></DialogHeader>
                        <Textarea value={reportContent} onChange={(e) => setReportContent(e.target.value)} placeholder="Motif du signalement..." rows={4} />
                        <DialogFooter>
                          <Button variant="ghost" onClick={() => setReportTarget(null)}>Annuler</Button>
                          <Button onClick={submitReport} data-testid="submit-report">Envoyer</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reply */}
      <div id="reply-box" className="mt-8">
        {!user ? (
          <Card><CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground mb-3">Connectez-vous pour répondre à ce sujet.</p>
            <SteamLoginButton />
          </CardContent></Card>
        ) : t.locked && !isStaff ? (
          <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">Ce sujet est verrouillé.</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-5 space-y-3">
              {quoteId && (
                <div className="flex items-center justify-between bg-secondary rounded px-3 py-2 text-xs">
                  <span className="text-muted-foreground">Vous citez un message.</span>
                  <button onClick={() => setQuoteId(null)} className="text-primary hover:underline">Retirer la citation</button>
                </div>
              )}
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Votre réponse..."
                rows={5}
                data-testid="reply-textarea"
              />
              <p className="text-xs text-muted-foreground">
                Astuce : mentionnez un utilisateur avec @{user.persona_name.split(" ")[0]}.
              </p>
              <div className="flex justify-end">
                <Button onClick={submitReply} disabled={reply.trim().length < 1} data-testid="submit-reply-button">Publier la réponse</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
